import { getMockExamQuestionListApi } from "@/api/services/admin/learning-content";
import { TGetMockExamQuestionListItemDto } from "@/api/services/admin/learning-content/dtos/get-mock-exam-question-list.dto";
import { DATE_FORMAT } from "@/constants/datetime";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { SUBJECT_TABLE_PER_PAGE } from "@/features/learning-content/constants/pagination";
import { TMockExamQuestionData } from "@/features/learning-content/types/learningContent";
import { mapExplanationsToLanguages } from "@/features/learning-content/utils/map-explanations-to-languages";
import { dayjs } from "@/utils/dayjs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

// Convert API item to frontend display type (matches design STUDY_ADM_LCM-MockExamShow-01)
const mapItem = (item: TGetMockExamQuestionListItemDto): TMockExamQuestionData => ({
  id: item.id,
  questionId: item.sheetRowId != null ? String(item.sheetRowId) : "",
  subjectName: item.subjectName ?? "",
  questionText: item.questionText ?? "",
  hasImage: !!item.imageFile,
  imageUrl: item.imageFile ?? undefined,
  correctAnswer: item.correctAnswer ?? "",
  supportedLanguages: mapExplanationsToLanguages(item.explanations),
  score: item.score ?? null,
  addedDate: item.createdAt ? dayjs(item.createdAt).format(DATE_FORMAT) : "",
});

/**
 * Custom hook for fetching paginated mock exam question list by mock exam ID.
 */
export const useMockExamQuestionList = (industryId?: string, mockExamId?: string) => {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_MOCK_EXAM_QUESTION_LIST, industryId, mockExamId, page],
    queryFn: () => getMockExamQuestionListApi(industryId!, mockExamId!, { page, perPage: SUBJECT_TABLE_PER_PAGE }),
    enabled: !!industryId && !!mockExamId,
  });

  const response = query.data;
  const pagination = response?.pagination;
  const items = response?.data ?? [];

  const data = useMemo(() => items.map(mapItem), [items]);

  // Invalidate all pages after sync/import
  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_MOCK_EXAM_QUESTION_LIST, industryId, mockExamId] });
  };

  return {
    data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    page,
    totalPage: pagination?.totalPage ?? 1,
    totalCount: pagination?.total ?? items.length,
    onPageChange: setPage,
    refetch,
  };
};
